/**
 * InputManager — Unified input handler for keyboard, mouse, touch, and gamepad.
 *
 * Detects current input method and provides action-based input queries.
 * Call update() each frame to poll gamepad state.
 *
 * @module InputManager
 */

import eventBus from '../core/EventBus.js';

/**
 * @typedef {'mouse'|'keyboard'|'gamepad'|'touch'} InputMethod
 */

/**
 * Directional action bindings: keyboard keys mapped to directions.
 * @type {Object<string, string>}
 */
const KEY_TO_DIRECTION = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  w: 'up',
  W: 'up',
  s: 'down',
  S: 'down',
  a: 'left',
  A: 'left',
  d: 'right',
  D: 'right',
};

/**
 * Action bindings: action name -> array of keyboard keys that trigger it.
 * @type {Object<string, string[]>}
 */
const ACTION_KEYS = {
  confirm: ['Enter', 'e', 'E'],
  cancel: ['Escape'],
  inventory: ['i', 'I'],
  quest: ['j', 'J'],
  character: ['c', 'C'],
  map: ['m', 'M'],
  menu: ['Escape'],
  debug: ['`', '~'],
};

/**
 * Gamepad button indices (standard mapping) to actions.
 * @type {Object<string, number[]>}
 */
const ACTION_GAMEPAD_BUTTONS = {
  confirm: [0],       // A / Cross
  cancel: [1],        // B / Circle
  interact: [2],      // X / Square
  inventory: [3],     // Y / Triangle
  prevTab: [4],       // LB / L1
  nextTab: [5],       // RB / R1
  menu: [9],          // Start
  quickMenu: [8],     // Select
  quest: [12],        // D-pad Up
  character: [13],    // D-pad Down
  map: [15],          // D-pad Right
};

/**
 * Gamepad d-pad button indices to directions.
 * @type {Object<number, string>}
 */
const DPAD_TO_DIRECTION = {
  12: 'up',
  13: 'down',
  14: 'left',
  15: 'right',
};

/** Minimum axis threshold for stick input. */
const STICK_DEADZONE = 0.4;

/** Minimum time between repeated stick/dpad navigation events (ms). */
const NAV_REPEAT_DELAY = 200;

/** Minimum swipe distance in pixels. */
const SWIPE_THRESHOLD = 30;

/** Maximum swipe duration in ms. */
const SWIPE_MAX_TIME = 500;

class InputManager {
  constructor() {
    /** @type {InputMethod} */
    this._inputMethod = 'mouse';

    /** Currently pressed keyboard keys. @type {Set<string>} */
    this._keysDown = new Set();

    /** Keys pressed this frame (for single-fire detection). @type {Set<string>} */
    this._keysJustPressed = new Set();

    /** Mouse position. @type {{x: number, y: number}} */
    this._mouse = { x: 0, y: 0 };

    /** Mouse buttons currently held. @type {Set<number>} */
    this._mouseButtons = new Set();

    /** Active touches. @type {Map<number, {startX: number, startY: number, startTime: number, x: number, y: number}>} */
    this._touches = new Map();

    /** Action callbacks. @type {Map<string, Set<Function>>} */
    this._actionCallbacks = new Map();

    /** Previous gamepad button states for edge detection. @type {boolean[]} */
    this._prevGamepadButtons = [];

    /** Timestamp of last navigation repeat. */
    this._lastNavTime = 0;

    /** Previous gamepad stick direction for edge detection. @type {string|null} */
    this._prevStickDirection = null;

    /** Whether L3 is pressed. */
    this._gamepadL3 = false;

    /** Whether R3 is pressed. */
    this._gamepadR3 = false;

    /** Reference to FocusManager, set via setFocusManager(). @type {object|null} */
    this._focusManager = null;

    /** Track if listeners are bound. */
    this._bound = false;
  }

  /**
   * Initialize event listeners. Safe to call multiple times.
   */
  init() {
    if (this._bound) return;
    this._bound = true;

    // Keyboard
    window.addEventListener('keydown', this._onKeyDown.bind(this));
    window.addEventListener('keyup', this._onKeyUp.bind(this));

    // Mouse
    window.addEventListener('mousemove', this._onMouseMove.bind(this));
    window.addEventListener('mousedown', this._onMouseDown.bind(this));
    window.addEventListener('mouseup', this._onMouseUp.bind(this));

    // Touch
    window.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: false });
    window.addEventListener('touchmove', this._onTouchMove.bind(this), { passive: false });
    window.addEventListener('touchend', this._onTouchEnd.bind(this), { passive: false });
    window.addEventListener('touchcancel', this._onTouchCancel.bind(this));

    // Gamepad
    window.addEventListener('gamepadconnected', this._onGamepadConnected.bind(this));
    window.addEventListener('gamepaddisconnected', this._onGamepadDisconnected.bind(this));
  }

  /**
   * Set the FocusManager instance for navigation integration.
   * @param {object} focusManager
   */
  setFocusManager(focusManager) {
    this._focusManager = focusManager;
  }

  // ──────────────────────────────────────────
  // Input method detection
  // ──────────────────────────────────────────

  /**
   * Get the current input method.
   * @returns {InputMethod}
   */
  getInputMethod() {
    return this._inputMethod;
  }

  /**
   * Set the current input method if it changed, emitting an event.
   * @param {InputMethod} method
   */
  _setInputMethod(method) {
    if (this._inputMethod === method) return;
    this._inputMethod = method;
    eventBus.emit('inputmethod:changed', method);
  }

  // ──────────────────────────────────────────
  // Keyboard
  // ──────────────────────────────────────────

  /** @param {KeyboardEvent} e */
  _onKeyDown(e) {
    // Ignore repeated key events from holding a key
    if (e.repeat) return;

    this._setInputMethod('keyboard');
    this._keysDown.add(e.key);
    this._keysJustPressed.add(e.key);

    // Direction navigation
    const dir = KEY_TO_DIRECTION[e.key];
    if (dir && this._focusManager) {
      this._focusManager.navigate(dir);
      e.preventDefault();
      return;
    }

    // Tab navigation
    if (e.key === 'Tab' && this._focusManager) {
      if (e.shiftKey) {
        this._focusManager.navigateLinear(-1);
      } else {
        this._focusManager.navigateLinear(1);
      }
      e.preventDefault();
      return;
    }

    // Fire action callbacks
    this._fireActionsForKey(e.key);
  }

  /** @param {KeyboardEvent} e */
  _onKeyUp(e) {
    this._keysDown.delete(e.key);
  }

  // ──────────────────────────────────────────
  // Mouse
  // ──────────────────────────────────────────

  /** @param {MouseEvent} e */
  _onMouseMove(e) {
    this._setInputMethod('mouse');
    this._mouse.x = e.clientX;
    this._mouse.y = e.clientY;
  }

  /** @param {MouseEvent} e */
  _onMouseDown(e) {
    this._setInputMethod('mouse');
    this._mouseButtons.add(e.button);
  }

  /** @param {MouseEvent} e */
  _onMouseUp(e) {
    this._mouseButtons.delete(e.button);
  }

  // ──────────────────────────────────────────
  // Touch
  // ──────────────────────────────────────────

  /** @param {TouchEvent} e */
  _onTouchStart(e) {
    this._setInputMethod('touch');
    for (const touch of e.changedTouches) {
      this._touches.set(touch.identifier, {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: performance.now(),
        x: touch.clientX,
        y: touch.clientY,
      });
    }
  }

  /** @param {TouchEvent} e */
  _onTouchMove(e) {
    for (const touch of e.changedTouches) {
      const t = this._touches.get(touch.identifier);
      if (t) {
        t.x = touch.clientX;
        t.y = touch.clientY;
      }
    }
  }

  /** @param {TouchEvent} e */
  _onTouchEnd(e) {
    for (const touch of e.changedTouches) {
      const t = this._touches.get(touch.identifier);
      if (t) {
        this._processSwipe(t);
        this._touches.delete(touch.identifier);
      }
    }

    // Two-finger tap = cancel
    if (e.touches.length === 0 && e.changedTouches.length === 2) {
      this._fireAction('cancel');
    }
  }

  /** @param {TouchEvent} e */
  _onTouchCancel(e) {
    for (const touch of e.changedTouches) {
      this._touches.delete(touch.identifier);
    }
  }

  /**
   * Detect swipe gestures from a completed touch.
   * @param {{startX: number, startY: number, startTime: number, x: number, y: number}} t
   */
  _processSwipe(t) {
    const dx = t.x - t.startX;
    const dy = t.y - t.startY;
    const elapsed = performance.now() - t.startTime;

    if (elapsed > SWIPE_MAX_TIME) return;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    if (absDx < SWIPE_THRESHOLD && absDy < SWIPE_THRESHOLD) {
      // Tap, not swipe
      return;
    }

    let direction;
    if (absDx > absDy) {
      direction = dx > 0 ? 'right' : 'left';
    } else {
      direction = dy > 0 ? 'down' : 'up';
    }

    eventBus.emit('touch:swipe', direction);
    if (this._focusManager) {
      this._focusManager.navigate(direction);
    }
  }

  // ──────────────────────────────────────────
  // Gamepad
  // ──────────────────────────────────────────

  /** @param {GamepadEvent} e */
  _onGamepadConnected(e) {
    this._setInputMethod('gamepad');
    console.log(`[InputManager] Gamepad connected: ${e.gamepad.id}`);
  }

  /** @param {GamepadEvent} e */
  _onGamepadDisconnected(e) {
    console.log(`[InputManager] Gamepad disconnected: ${e.gamepad.id}`);
  }

  /**
   * Poll gamepad state. Call once per frame.
   */
  _pollGamepad() {
    const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
    /** @type {Gamepad|null} */
    let gp = null;
    for (const pad of gamepads) {
      if (pad && pad.connected) {
        gp = pad;
        break;
      }
    }
    if (!gp) return;

    const buttons = gp.buttons;
    const now = performance.now();
    let hasGamepadInput = false;

    // Edge-detect button presses
    for (let i = 0; i < buttons.length; i++) {
      const pressed = buttons[i].pressed;
      const wasPressed = this._prevGamepadButtons[i] || false;

      if (pressed) hasGamepadInput = true;

      if (pressed && !wasPressed) {
        // D-pad directions
        const dir = DPAD_TO_DIRECTION[i];
        if (dir && this._focusManager) {
          this._focusManager.navigate(dir);
        }

        // Action buttons
        for (const [action, indices] of Object.entries(ACTION_GAMEPAD_BUTTONS)) {
          if (indices.includes(i)) {
            this._fireAction(action);
          }
        }
      }

      this._prevGamepadButtons[i] = pressed;
    }

    // L3 + R3 = debug
    const l3 = buttons[10]?.pressed || false;
    const r3 = buttons[11]?.pressed || false;
    if (l3 && r3 && !(this._gamepadL3 && this._gamepadR3)) {
      this._fireAction('debug');
    }
    this._gamepadL3 = l3;
    this._gamepadR3 = r3;

    // Left stick navigation with repeat delay
    const lx = gp.axes[0] || 0;
    const ly = gp.axes[1] || 0;
    let stickDir = null;

    if (Math.abs(lx) > STICK_DEADZONE || Math.abs(ly) > STICK_DEADZONE) {
      hasGamepadInput = true;
      if (Math.abs(lx) > Math.abs(ly)) {
        stickDir = lx > 0 ? 'right' : 'left';
      } else {
        stickDir = ly > 0 ? 'down' : 'up';
      }
    }

    if (stickDir) {
      if (stickDir !== this._prevStickDirection || (now - this._lastNavTime) > NAV_REPEAT_DELAY) {
        if (this._focusManager) {
          this._focusManager.navigate(stickDir);
        }
        this._lastNavTime = now;
      }
    }
    this._prevStickDirection = stickDir;

    // Only switch to gamepad method when there's actual input
    if (hasGamepadInput) {
      this._setInputMethod('gamepad');
    }
  }

  // ──────────────────────────────────────────
  // Action system
  // ──────────────────────────────────────────

  /**
   * Fire all callbacks registered for an action.
   * @param {string} action
   */
  _fireAction(action) {
    // Special handling: confirm/cancel go to FocusManager
    if (this._focusManager) {
      if (action === 'confirm') {
        this._focusManager.confirm();
      }
      if (action === 'cancel') {
        this._focusManager.cancel();
      }
    }

    const cbs = this._actionCallbacks.get(action);
    if (cbs) {
      for (const cb of cbs) {
        try {
          cb();
        } catch (err) {
          console.error(`[InputManager] Error in action callback "${action}":`, err);
        }
      }
    }

    eventBus.emit(`action:${action}`);
  }

  /**
   * Check which actions a keyboard key triggers and fire them.
   * @param {string} key
   */
  _fireActionsForKey(key) {
    for (const [action, keys] of Object.entries(ACTION_KEYS)) {
      if (keys.includes(key)) {
        this._fireAction(action);
      }
    }
  }

  // ──────────────────────────────────────────
  // Public API
  // ──────────────────────────────────────────

  /**
   * Check if a keyboard key is currently pressed.
   * @param {string} key — The KeyboardEvent.key value.
   * @returns {boolean}
   */
  isPressed(key) {
    return this._keysDown.has(key);
  }

  /**
   * Check if any key bound to an action is currently active.
   * @param {string} action
   * @returns {boolean}
   */
  isActionActive(action) {
    const keys = ACTION_KEYS[action];
    if (keys) {
      for (const key of keys) {
        if (this._keysDown.has(key)) return true;
      }
    }
    return false;
  }

  /**
   * Get the current directional input as a normalized vector.
   * Returns {x: 0, y: 0} if no directional input.
   * @returns {{x: number, y: number}}
   */
  getDirection() {
    let x = 0;
    let y = 0;

    // Keyboard
    if (this._keysDown.has('ArrowLeft') || this._keysDown.has('a') || this._keysDown.has('A')) x -= 1;
    if (this._keysDown.has('ArrowRight') || this._keysDown.has('d') || this._keysDown.has('D')) x += 1;
    if (this._keysDown.has('ArrowUp') || this._keysDown.has('w') || this._keysDown.has('W')) y -= 1;
    if (this._keysDown.has('ArrowDown') || this._keysDown.has('s') || this._keysDown.has('S')) y += 1;

    // Gamepad stick (check live)
    if (x === 0 && y === 0) {
      const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
      for (const gp of gamepads) {
        if (gp && gp.connected) {
          const lx = gp.axes[0] || 0;
          const ly = gp.axes[1] || 0;
          if (Math.abs(lx) > STICK_DEADZONE) x = Math.sign(lx);
          if (Math.abs(ly) > STICK_DEADZONE) y = Math.sign(ly);
          break;
        }
      }
    }

    return { x, y };
  }

  /**
   * Get the current mouse position.
   * @returns {{x: number, y: number}}
   */
  getMousePosition() {
    return { ...this._mouse };
  }

  /**
   * Register a callback for an action.
   * @param {string} action
   * @param {Function} callback
   */
  onAction(action, callback) {
    if (!this._actionCallbacks.has(action)) {
      this._actionCallbacks.set(action, new Set());
    }
    this._actionCallbacks.get(action).add(callback);
  }

  /**
   * Unregister a callback for an action.
   * @param {string} action
   * @param {Function} callback
   */
  offAction(action, callback) {
    const cbs = this._actionCallbacks.get(action);
    if (cbs) {
      cbs.delete(callback);
    }
  }

  /**
   * Call once per frame to poll gamepad and flush per-frame state.
   */
  update() {
    this._pollGamepad();
    this._keysJustPressed.clear();
  }
}

/** Singleton instance. */
const inputManager = new InputManager();
export default inputManager;
